import { useCallback, useEffect, useRef, type ReactElement } from "react";
import { useLocation } from "react-router";
import { LegacyManagedPointer } from "./legacy-managed-pointer";
import { useLegacyOwnedRejection } from "./write-authority-provider";

/**
 * T036 shell host: renders the shared pointer for the latest 409
 * `DOMAIN_LEGACY_OWNED` rejection a transport reported and no mounted
 * surface claimed (U1). Dismissible, and cleared on route change so a notice
 * never follows the user to another screen. The rejected mutation is never
 * replayed from here.
 */
export function LegacyOwnedRejectionHost(): ReactElement | null {
  const { rejection, dismissLegacyOwnedRejection } = useLegacyOwnedRejection();
  const { pathname } = useLocation();
  const lastPathnameRef = useRef(pathname);

  useEffect(() => {
    if (lastPathnameRef.current !== pathname) {
      lastPathnameRef.current = pathname;
      dismissLegacyOwnedRejection();
    }
  }, [dismissLegacyOwnedRejection, pathname]);

  const dismiss = useCallback(() => {
    dismissLegacyOwnedRejection();
    focusPageStart();
  }, [dismissLegacyOwnedRejection]);

  if (rejection === null) {
    return null;
  }

  return (
    <div className="clm-page-gutter pt-4" data-testid="legacy-owned-rejection">
      {/* The host precedes the routed page's h1 in DOM order: no heading (U8). */}
      <LegacyManagedPointer headingLevel="none" onDismiss={dismiss} />
    </div>
  );
}

/**
 * U9: the Dismiss button unmounts with the pointer, so focus is handed to the
 * page heading (else the main landmark) instead of dropping to body.
 */
function focusPageStart(): void {
  const main = document.querySelector("main");
  const target = main?.querySelector<HTMLElement>("h1") ?? main;
  if (target === null || target === undefined) {
    return;
  }
  if (!target.hasAttribute("tabindex")) {
    target.tabIndex = -1;
  }
  target.focus();
}
